











































































































import { Vue, Component, Watch } from 'vue-property-decorator'
import { MATERIAL_TYPES, REPORT_TYPES } from '@/constants/schema-constants'
import type { ReportType } from '@/constants/schema-constants'

import { FileUploader } from '@/libs/file-uploader'

import { MaterialFormInputDto } from '@/dtos/commons'
import { ReportFilesPostDto, ReportFilesPostResponse, ReportFilesPostRequest } from '@/dtos/report-files/post'

import { staticRoutes } from '@/routes'

import { errorsModule } from '@/stores/errors'
import { reportFilesModule } from '@/stores/report-files-store'

const uploadThenPost = async <T, >(rawReq: T, exec: (_: T) => Promise<void>) => {
  const uploader = new FileUploader()
  const req = await uploader.prepare(rawReq)

  await exec(req)
}

@Component({
  components: {
    SmBtn: () => import('@/components/atoms/SmBtn.vue'),
    SmSelect: () => import('@/components/atoms/SmSelect.vue'),
    SmText: () => import('@/components/atoms/SmText.vue'),

    SmDialogText: () => import('@/components/organisms/dialog/SmDialogText.vue'),

    SmDatePickersMonth: () => import('@/components/molecules/SmDatePickersMonth.vue'),
    SmMaterialInputArea: () => import('@/components/molecules/SmMaterialInputArea.vue'),

    SmTemplate: () => import('@/components/templates/SmTemplate.vue'),
  }
})
export default class ReportFileUploadPage extends Vue {
  MATERIAL_TYPES = Object.freeze(MATERIAL_TYPES)
  REPORT_TYPES = Object.freeze(REPORT_TYPES)

  readonly UPLOAD_ORDERS = [1, 2, 3]

  files: File[] = []
  errorMessage = ''
  // 対象年月の初期値は前月
  targetDate: string = this.getPreviousYearMonth()
  selectedReportType: ReportType | null = null
  selectUploadOrder = 1
  isReportUploadFailed = false
  uploadFailedMessage = ''
  uploadFailedCaption = ''

  selectionReportTypes = [
    { value: null, label: '選択する' },
    { value: REPORT_TYPES.MANAGEMENT, label: '管理状況報告書' },
    { value: REPORT_TYPES.ADMINISTRATIVE, label: '月次管理事務報告書' },
    { value: REPORT_TYPES.PAYMENT_APPROVAL, label: '振込承認依頼書' },
  ]

  get selectionUploadOrder(): { value: number; label: string }[] {
    return this.UPLOAD_ORDERS.map(order => ({ value: order, label: `${order}回目` }))
  }

  get isUploadButtonDisabled(): boolean {
    // ファイルが選択されていない場合、またはエラーメッセージが存在する場合はボタンを無効化
    return this.files.length === 0 || this.errorMessage !== ''
  }

  get reportTypeLabel(): string | undefined {
    if (!this.selectedReportType) return
    return this.selectedReportTypeLabel.replace(/\s+/g, '')
  }

  get selectedReportTypeLabel(): string {
    const selectedItem = this.selectionReportTypes.find(reportType => reportType.value === this.selectedReportType)
    return selectedItem ? selectedItem.label : '選択する'
  }

  get computedFileNamePattern(): string | undefined {
    if (!this.selectedReportType) return

    const reportTypeLabelWithoutSpaces = this.selectedReportTypeLabel.replace(/\s+/g, '')
    return `^(\\d{6})_(\\d{2})_(.+)_${reportTypeLabelWithoutSpaces}\\.pdf`
  }

  @Watch('selectedReportType')
  onSelectedReportTypeChanged(newValue: ReportType): void {
    this.errorMessage = ''
    if (newValue === REPORT_TYPES.MANAGEMENT || newValue === REPORT_TYPES.ADMINISTRATIVE) {
      this.selectUploadOrder = 1
    }
  }

  // 前月の年月を取得
  getPreviousYearMonth(): string {
    const now = new Date()
    // 月末の場合に計算がズレてしまうので1日を指定
    // 例: 3月31日だと計算結果は3月3日になる
    now.setDate(1)
    now.setMonth(now.getMonth() - 1)

    const year = now.getFullYear()
    const month = now.getMonth()
    const lastMonth = ('0' + (month + 1)).slice(-2)
    return `${year}-${lastMonth}`
  }

  // ファイルの添付の上限数は現状（2025/06/30リリース時点）では設定しない方針とした
  // TODO：今後の物件増加数次第で設定を検討すること
  async uploadFile(): Promise<void> {
    errorsModule.clearGlobalErrors()
    if (!this.files.length) return

    const validDtos: ReportFilesPostDto[] = []
    let year = ''
    let month = ''
    if (this.targetDate) {
      [year, month] = this.targetDate.split('-')
    }

    if (!this.selectedReportType || !year || !month) return

    const renamedFileName = `${year}年${month}月_${this.selectedReportTypeLabel}.pdf`

    for (const file of this.files) {
      // ファイルのリネーム
      const renamedFile = new File([file], renamedFileName, { type: file.type })

      const materialDto = new MaterialFormInputDto()
      materialDto.file = renamedFile
      materialDto.originalFileNameInitialCopy = file.name

      const dto = new ReportFilesPostDto()
      dto.oldFileName = file.name
      dto.material = materialDto

      validDtos.push(dto)
    }

    const req = new ReportFilesPostRequest(validDtos, this.selectedReportType, `${year}-${month}`, this.selectUploadOrder)
    await uploadThenPost(req, reportFilesModule.postReportFiles)

    const response = reportFilesModule.postReportFilesResponse
    if (response?.errors?.length) {
      this.uploadFailedMessage = this.getReportUploadFailedMessage(response)
      this.uploadFailedCaption = this.getReportUploadFailedCaption(response)
      this.isReportUploadFailed = true
    } else {
      // 報告書一覧検索画面へ遷移する
      this.goToReportFilesList()
    }
  }

  goToReportFilesList(): void {
    this.isReportUploadFailed = false
    this.$router.replace({ name: staticRoutes.reportFilesList.name, query: {} })
  }

  getReportUploadFailedMessage(response: ReportFilesPostResponse): string {
    const failedCount = response?.errors?.reduce((total, error) => {
      const filenames = error.errorFileNameList?.flatMap(str => str.split(',').map(s => s.trim())) || []
      return total + filenames.length
    }, 0) || 0

    return `${failedCount}件のファイルがアップロードできませんでした。`
  }

  getReportUploadFailedCaption(response: ReportFilesPostResponse): string {
    if (!response?.errors?.length) return ''

    return response.errors.map(error => {
      const fileNames = error.errorFileNameList
        ?.flatMap(str => str.split(',').map(s => s.trim()))
        .filter(name => name) || []

      const fileListText = fileNames.map(name => `・${name}`).join('\n')
      return `${error.errorMessage}\n${fileListText}`
    }).join('\n\n')
  }

  closeDialog(): void {
    this.isReportUploadFailed = false
    this.files = []
    this.targetDate = this.getPreviousYearMonth()
    this.selectUploadOrder = 1
  }
}
